/* eslint-disable max-len */
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	signal,
	SimpleChanges,
	WritableSignal
} from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ModalController, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import * as _ from "lodash-es";
import { DateTime } from "luxon";
import { Subscription, filter, firstValueFrom, take } from "rxjs";
import { IEvent, IModule, IEventUser } from "src/app/shared/interfaces";
import {
	ICardExchangeField,
	ICardExchangeFieldResult,
	ICardExchangeForm,
	ICardExchangeFieldResultData
} from "src/app/shared/interfaces/card-exchange.interfaces";
import {
	AnalyticsService,
	UtilityService,
	StorageService,
	FirestoreService,
	DocumentsService,
	FileService
} from "src/app/shared/services";
import { CardExchangeService } from "src/app/shared/services/card-exchange.service";
import { LogFuncTrace } from "src/app/shared/utils-func/debug-decorators";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";

@Component({
	selector: "app-card-exchange-form",
	templateUrl: "./card-exchange-form.component.html",
	styleUrls: ["./card-exchange-form.component.scss"],
	standalone: false
})
export class CardExchangeFormComponent implements OnInit, OnDestroy, OnChanges {
	subscriptions: Subscription[] = [];
	contactDatasSub: Subscription;
	formSub: Subscription;
	formLoader: WritableSignal<boolean> = signal(false);

	componentLoader: boolean = true;
	loaderDoc: boolean = false;
	loadingSend: boolean = false;

	@Input() componentMode: boolean = false;
	@Output() changeView: EventEmitter<{ segmentType: string; type: string; uid: string }> = new EventEmitter();

	showBackBtn: boolean;
	event: IEvent;
	module: IModule;
	myEventUser: IEventUser;
	form: ICardExchangeForm;
	formId: string;
	contactDatas: ICardExchangeFieldResultData[] = [];
	eventUserResultDatas: ICardExchangeFieldResultData[] = [];
	eventUserContactId: string;
	eventUserContact: IEventUser;

	viewOnly: boolean = true;

	isMobile: boolean = false;

	fieldsForm: UntypedFormGroup;

	locale: string = "";
	currentLanguage: string = environment.platform.defaultLanguage;
	device: string;

	// --- State for pending file operations ---
	filesToUpload: { [fieldId: string]: File } = {};
	fieldsToDelete: { [fieldId: string]: { uid: string; path: string } } = {};
	documentUIState: {
		[fieldId: string]: { name: string; type: string; assetFileName: string; previewUrl?: string | null };
	} = {};
	// --- End State ---

	constructor(
		private SAnalytics: AnalyticsService,
		private modalCtrl: ModalController,
		private platform: Platform,
		private store: Store,
		private SSCardExchange: CardExchangeService,
		private STranslate: TranslateService,
		private SUtility: UtilityService,
		private SStorage: StorageService,
		private fb: UntypedFormBuilder,
		private SFirestore: FirestoreService,
		private snackbar: MatSnackBar,
		private FileService: FileService,
		public SDocument: DocumentsService
	) {
		this.fieldsForm = this.fb.group({});
	}

	ngOnInit() {
		this.isMobile =
			this.platform.is("mobile") || this.platform.is("mobileweb") || window.innerWidth < 768 ? true : false;

		this.device = this.platform.is("android") ? "android" : this.platform.is("ios") ? "ios" : "desktop";

		this.currentLanguage = this.STranslate.currentLang;
		if (this.myEventUser && this.myEventUser?.groups?.length > 0) {
			this.locale = this.SUtility.getLocale(
				this.myEventUser ? this.myEventUser.updatedSettings.language : this.event.language
			);
			this.getForm();
		} else {
			this.closeModal();
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes && changes.formId && !changes.formId.firstChange) {
			if (this.myEventUser && this.myEventUser?.groups?.length > 0 && this.eventUserContact) {
				this.getForm();
			}
		}
	}

	ionViewWillLeave() {
		if (!this.componentMode) {
			this.form = null;
		}
		this.subscriptions.concat([this.contactDatasSub, this.formSub]).forEach((sub) => sub?.unsubscribe());
	}

	/**
	 * Unsubscribe all subscriptions
	 */
	ngOnDestroy() {
		this.subscriptions.concat([this.contactDatasSub, this.formSub]).forEach((sub) => sub?.unsubscribe());
	}

	/**
	 * Get form
	 */
	getForm() {
		if (this.formSub && !this.formSub.closed) {
			this.formSub.unsubscribe();
		}

		this.formSub = this.SSCardExchange.getSpecificFormByGroupId(
			this.event.uid,
			this.module.uid,
			this.myEventUser.groups
		)
			.pipe(
				filter((form) => !!form?.visibility),
				take(1)
			)
			.subscribe({
				next: (form) => {
					if (!form) {
						console.warn("No visible form found for user groups.");
						this.closeModal("unavailable");
						return;
					}

					if (!_.isEqual(this.form?.uid, form.uid) || !this.form) {
						this.form = form;
						this.form.fields = this.form.fields.filter((field) => field.visibility);

						if (this.form.fields.length === 0) {
							this.closeModal("unavailable");
							return;
						}

						this.initFieldsForm();

						this.getContactDatas();
					} else {
						this.getContactDatas();
					}
				},
				error: (error) => {
					console.error("🚀 ~ CardExchangeFormComponent ~ getForm error:", error);
					this.closeModal("error");
				}
			});
	}

	/**
	 * getContactDatas
	 * @param eventId
	 * @param moduleId
	 * @param formId
	 * @param userId
	 */
	getContactDatas() {
		if (this.contactDatasSub && !this.contactDatasSub.closed) {
			this.contactDatasSub?.unsubscribe();
		}

		this.contactDatasSub = this.SSCardExchange.getSpecificContactFormDatasOfUser(
			this.event.uid,
			this.myEventUser.moduleId,
			this.myEventUser.uid,
			this.eventUserContactId
		).subscribe({
			next: (datas: ICardExchangeFieldResultData[]) => {
				this.contactDatas = datas || [];

				if (this.form && this.form.fields.length > 0) {
					this.patchResultsForm();
				}
				this.componentLoader = false;
			},
			error: (error) => {
				console.error("🚀 ~ CardExchangeFormComponent ~ getContactDatas error:", error);
				this.contactDatas = [];
				this.patchResultsForm();
				this.componentLoader = false;
				this.snackbar.open(this.STranslate.instant("snackbar.error_fetching_data"), "", {
					duration: 3000,
					panelClass: "error-snackbar"
				});
			}
		});
	}

	/**
	 * initFieldsForm
	 */
	initFieldsForm() {
		Object.keys(this.fieldsForm.controls).forEach((key) => {
			this.fieldsForm.removeControl(key);
		});
		this.documentUIState = {};

		this.form.fields.forEach((field) => {
			if (field.type === "oneSelect") {
				this.fieldsForm.addControl(field.uid, new UntypedFormControl(""));
			} else if (field.type === "multipleSelect") {
				const group = new UntypedFormGroup({});
				field.answers.forEach((answer) => {
					group.addControl(answer.uid, new UntypedFormControl(false));
				});
				this.fieldsForm.addControl(field.uid, group);
			} else if (field.type === "dissertative") {
				this.fieldsForm.addControl(field.uid, new UntypedFormControl(""));
			} else if (field.type === "date") {
				this.fieldsForm.addControl(field.uid, new UntypedFormControl(""));
			} else if (field.type === "document") {
				this.fieldsForm.addControl(field.uid, new UntypedFormControl(""));
				this.documentUIState[field.uid] = {
					name: "",
					type: "",
					assetFileName: "generic-file_ico",
					previewUrl: null
				};
			} else if (field.type === "evaluation") {
				this.fieldsForm.addControl(field.uid, new UntypedFormControl(0));
			} else if (field.type === "plainText") {
				this.fieldsForm.addControl(field.uid, new UntypedFormControl(""));
			}
		});
	}

	/**
	 * Patch form with all form result
	 */
	patchResultsForm() {
		this.form.fields.forEach((field) => {
			if (!this.fieldsForm.controls[field.uid]) {
				console.warn(`Control not found for field ${field.uid} during patch`);
				return;
			}

			const resultOfField = this.getresultOfField(field);

			if (field.type === "oneSelect") {
				this.fieldsForm
					.get(field.uid)
					.patchValue(resultOfField?.oneSelect?.answerId ?? "", { emitEvent: false });
			} else if (field.type === "multipleSelect") {
				const multipleSelectValues = {};
				field.answers.forEach((answer) => {
					multipleSelectValues[answer.uid] =
						resultOfField?.multipleSelect?.answersIds?.includes(answer.uid) ?? false;
				});
				this.fieldsForm.get(field.uid).patchValue(multipleSelectValues, { emitEvent: false });
			} else if (field.type === "dissertative" || field.type === "plainText") {
				this.fieldsForm.get(field.uid).patchValue(resultOfField?.dissertative ?? "", { emitEvent: false });
			} else if (field.type === "date") {
				this.fieldsForm.get(field.uid).patchValue(resultOfField?.date ?? "", { emitEvent: false });
			} else if (field.type === "document") {
				const docData = resultOfField?.document;
				this.fieldsForm.get(field.uid).patchValue(docData?.url ?? "", { emitEvent: false });

				const isImage = docData?.docType?.includes("image") ?? false;
				this.documentUIState[field.uid] = {
					name: docData?.name ?? "",
					type: docData?.docType ?? "",
					assetFileName: docData?.docType
						? this.SDocument.getFomatForSVG(docData.docType)
						: "generic-file_ico",
					previewUrl: isImage ? docData?.url : null
				};
			} else if (field.type === "evaluation") {
				this.fieldsForm.get(field.uid).patchValue(resultOfField?.evaluation ?? 0, { emitEvent: false });
			}
		});
		this.filesToUpload = {};
		this.fieldsToDelete = {};
	}

	/**
	 * CHeck checkbox for multiple select
	 * @param field
	 */
	checkCheckbox(field: ICardExchangeField, answer: ICardExchangeFieldResult) {
		const groupValue = this.fieldsForm.get(field.uid)?.value;
		return groupValue ? groupValue[answer.uid] : false;
	}

	/**
	 * Update checkbox
	 * @param field
	 * @param answer
	 * @param event
	 */
	updateCheckboxs(field: ICardExchangeField, answer: ICardExchangeFieldResult, event: any) {
		const control = this.fieldsForm.get(field.uid)?.get(answer.uid);
		if (control) {
			control.patchValue(event.detail.checked);
		}
	}

	/**
	 * Check if document is an image
	 * @param field
	 * @returns
	 */
	checkImg(field: ICardExchangeField) {
		const result = this.getresultOfField(field);
		return (
			(result && result.document && result.document.docType.includes("image")) ||
			this.fieldsForm.get(field.uid).value.includes("image")
		);
	}

	/**
	 * Get result of field
	 * @param field
	 * @returns
	 */
	getresultOfField(field: ICardExchangeField) {
		return this.contactDatas.find(
			(result) => result.fieldId === field.uid && result.userId === this.myEventUser.uid
		);
	}

	/**
	 * Getting img from document type
	 * @param field
	 * @returns
	 */
	getImg(field: ICardExchangeField) {
		return this.fieldsForm.get(field.uid).value;
	}

	/**
	 * Get document
	 */
	async getDocument(evt: any, field: ICardExchangeField) {
		this.loaderDoc = true;
		const item: File = evt.target.files[0];
		evt.target.value = null;

		if (!item) {
			this.loaderDoc = false;
			return;
		}

		if (item.size > this.SDocument.CARD_EXCHANGE_DOC_SIZE_LIMIT) {
			this.loaderDoc = false;
			this.snackbar.open(
				this.STranslate.instant("snackbar.file_size_greater_than_limit", {
					limit: this.SDocument.CARD_EXCHANGE_DOC_SIZE_LIMIT / 1000000
				}),
				"",
				{
					duration: 3000,
					panelClass: "error-snackbar"
				}
			);
			return;
		}

		try {
			this.filesToUpload[field.uid] = item;
			delete this.fieldsToDelete[field.uid];

			let previewUrl: string | null = null;
			if (item.type.startsWith("image/")) {
				try {
					previewUrl = await this.SStorage.fromFileToBase64(item);
				} catch (previewError) {
					console.error("Error generating image preview:", previewError);
				}
			}

			this.documentUIState[field.uid] = {
				name: item.name,
				type: item.type,
				assetFileName: this.SDocument.getFomatForSVG(item.type),
				previewUrl: previewUrl
			};

			this.loaderDoc = false;
		} catch (error) {
			console.error("Error processing selected file:", error);
			this.loaderDoc = false;
			delete this.filesToUpload[field.uid];
			const originalResult = this.getresultOfField(field);
			const isOriginalImage = originalResult?.document?.docType?.includes("image") ?? false;
			this.documentUIState[field.uid] = {
				name: originalResult?.document?.name ?? "",
				type: originalResult?.document?.docType ?? "",
				assetFileName: originalResult?.document?.docType
					? this.SDocument.getFomatForSVG(originalResult.document.docType)
					: "generic-file_ico",
				previewUrl: isOriginalImage ? originalResult?.document?.url : null
			};
			this.snackbar.open(this.STranslate.instant("snackbar.error_processing_file"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * Delete document
	 */
	deleteDocument(field: ICardExchangeField) {
		const fieldId = field.uid;
		const existingResult = this.getresultOfField(field);
		const existingDoc = existingResult?.document;

		if (existingDoc?.url && existingDoc?.uid) {
			const path = this.buildStoragePath(existingDoc.uid, existingDoc.docType);
			this.fieldsToDelete[fieldId] = { uid: existingDoc.uid, path: path };
		}

		delete this.filesToUpload[fieldId];

		this.documentUIState[fieldId] = {
			name: "",
			type: "",
			assetFileName: "generic-file_ico",
			previewUrl: null
		};

		this.fieldsForm.get(fieldId).patchValue("");
	}

	/**
	 * Build storage path for card exchange documents.
	 */
	private buildStoragePath(documentId: string, fileType: string): string {
		const typeParts = typeof fileType === "string" ? fileType.split("/") : [];
		const mimeSubtype = typeParts.length > 1 ? typeParts[1] : null;
		const extension = mimeSubtype ? this.SDocument.checkType(mimeSubtype) : "bin";

		return `events/${this.event?.uid}/modules/${this.myEventUser.moduleId}/event-users/${this.myEventUser.uid}/card-exchange-datas/${this.eventUserContactId}/${documentId}.${extension}`;
	}

	/**
	 * Send form
	 */
	@LogFuncTrace
	async sendForm() {
		if (this.formLoader()) return;

		this.formLoader.set(true);
		const results: ICardExchangeFieldResultData[] = [];
		const datas = this.fieldsForm.getRawValue();

		try {
			for (const fieldId of Object.keys(datas)) {
				const formResultValue = datas[fieldId];
				const field = this.form.fields.find((f) => f.uid === fieldId);

				if (!field) continue;

				const existingResult = _.cloneDeep(this.getresultOfField(field));
				const finalResultData: Partial<ICardExchangeFieldResultData> = {};

				if (field.type === "document") {
					let documentData: ICardExchangeFieldResultData["document"] | null =
						existingResult?.document ?? null;
					const storagePath = existingResult?.document?.uid
						? this.buildStoragePath(existingResult.document.uid, existingResult.document.docType)
						: null;

					if (this.fieldsToDelete[fieldId]) {
						const deleteInfo = this.fieldsToDelete[fieldId];
						if (deleteInfo.path) {
							try {
								console.warn(`Deleted old file: ${deleteInfo.path}`);
							} catch (deleteError) {
								console.error(`Failed to delete file ${deleteInfo.path}:`, deleteError);
							}
						}
						documentData = null;
						delete this.fieldsToDelete[fieldId];
					}

					const fileToUpload = this.filesToUpload[fieldId];
					if (fileToUpload) {
						if (storagePath && !this.fieldsToDelete[fieldId]) {
							try {
								console.warn(`Deleted old file before replacement: ${storagePath}`);
							} catch (deleteError) {
								console.error(`Failed to delete file ${storagePath} before replacement:`, deleteError);
							}
						}

						const imageId = documentData?.uid || uuidv4();
						const newPath = this.buildStoragePath(imageId, fileToUpload.type);
						let imageUrl = "";

						try {
							const documentBase64 = await this.SStorage.fromFileToBase64(fileToUpload);
							const base64Content = documentBase64.split("base64,")[1];

							imageUrl = await this.SStorage.uploadBase64(base64Content, fileToUpload.type, newPath);

							documentData = {
								uid: imageId,
								docType: fileToUpload.type,
								name: fileToUpload.name,
								url: imageUrl
							};
						} catch (uploadError) {
							console.error(`Failed to upload file ${fileToUpload.name} to ${newPath}:`, uploadError);
							documentData = existingResult?.document ?? null;
							this.snackbar.open(
								this.STranslate.instant("snackbar.error_uploading_file", {
									fileName: fileToUpload.name
								}),
								"",
								{ duration: 3000, panelClass: "error-snackbar" }
							);
						}
						delete this.filesToUpload[fieldId];
					}

					finalResultData.document = documentData;
				} else if (field.type === "oneSelect") {
					finalResultData.oneSelect = { answerId: formResultValue };
				} else if (field.type === "multipleSelect") {
					finalResultData.multipleSelect = {
						answersIds: Object.entries(formResultValue || {})
							.filter(([, value]) => value)
							.map(([key]) => key)
					};
				} else if (field.type === "dissertative" || field.type === "plainText") {
					finalResultData.dissertative = formResultValue;
				} else if (field.type === "date") {
					finalResultData.date = formResultValue;
				} else if (field.type === "evaluation") {
					finalResultData.evaluation = formResultValue;
				}

				if (existingResult) {
					let dataChanged = false;
					if (field.type === "document") {
						if (!_.isEqual(finalResultData.document, existingResult.document)) {
							dataChanged = true;
						}
					} else {
						const key =
							field.type === "oneSelect"
								? "oneSelect"
								: field.type === "multipleSelect"
									? "multipleSelect"
									: field.type === "dissertative" || field.type === "plainText"
										? "dissertative"
										: field.type === "date"
											? "date"
											: field.type === "evaluation"
												? "evaluation"
												: null;
						if (key && !_.isEqual(finalResultData[key], existingResult[key])) {
							dataChanged = true;
						}
					}

					if (dataChanged) {
						const updatedResult: ICardExchangeFieldResultData = {
							...existingResult,
							...finalResultData
						};
						results.push(updatedResult);
					} else if (field.type === "document" && !finalResultData.document && existingResult.document) {
						const updatedResult: ICardExchangeFieldResultData = {
							...existingResult,
							document: null
						};
						results.push(updatedResult);
					}
				} else {
					let hasData = false;
					switch (field.type) {
						case "oneSelect":
							hasData = !!finalResultData.oneSelect?.answerId;
							break;
						case "multipleSelect":
							hasData = (finalResultData.multipleSelect?.answersIds?.length ?? 0) > 0;
							break;
						case "dissertative":
						case "plainText":
							hasData = !!finalResultData.dissertative;
							break;
						case "date":
							hasData = !!finalResultData.date;
							break;
						case "document":
							hasData = !!finalResultData.document?.url;
							break;
						case "evaluation":
							hasData = finalResultData.evaluation !== null && finalResultData.evaluation !== undefined;
							break;
					}

					if (hasData) {
						const newResult: ICardExchangeFieldResultData = {
							uid: this.SFirestore.createId(
								`events/${this.event?.uid}/modules/${this.myEventUser.moduleId}/event-users/${this.myEventUser.uid}/card-exchange-datas`
							),
							creationDate: DateTime.local().toISO(),
							userId: this.myEventUser.uid,
							contactId: this.eventUserContact.uid,
							formId: this.form.uid,
							fieldId: field.uid,
							type: field.type,
							oneSelect: null,
							multipleSelect: null,
							dissertative: null,
							date: null,
							document: null,
							evaluation: 0,
							...finalResultData
						};
						results.push(newResult);
					}
				}
			}

			if (results.length > 0) {
				await this.SSCardExchange.createOrUpdateCardExchangeResultDatas(
					this.event.uid,
					this.myEventUser.moduleId,
					this.myEventUser.uid,
					results
				);
				this.closeModal("success");
			} else {
				const onlyDeletions = Object.keys(this.fieldsToDelete).length > 0 && results.length === 0;
				if (onlyDeletions) {
					console.warn("Only file deletions performed, no data changes to save in Firestore.");
				} else {
					console.warn("Form submitted with no changes or data to save.");
				}
			}

			this.formLoader.set(false);
			this.filesToUpload = {};
			this.fieldsToDelete = {};
		} catch (error) {
			this.formLoader.set(false);
			console.error("🚀 ~ CardExchangeFormComponent ~ sendForm ~ error:", error);
			this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	/**
	 * closeModal
	 */
	async closeModal(type: "error" | "success" | "unavailable" | "emptyForm" | "cancelled" | "unknown" = "unknown") {
		this.filesToUpload = {};
		this.fieldsToDelete = {};
		this.documentUIState = {};

		try {
			let dismissData: any = { type: type };
			let messageKey: string | null = null;
			let panelClass = "info-snackbar";

			switch (type) {
				case "error":
					dismissData = { ...dismissData, error: true };
					messageKey = "snackbar.error_occured";
					panelClass = "error-snackbar";
					break;
				case "success":
					dismissData = { ...dismissData, success: true };
					break;
				case "unavailable":
					dismissData = { ...dismissData, unavailable: true };
					messageKey = "snackbar.form_unavailable";
					panelClass = "error-snackbar";
					break;
				case "emptyForm":
					dismissData = { ...dismissData, emptyForm: true };
					break;
				case "cancelled":
					dismissData = { ...dismissData, cancelled: true };
					messageKey = "";
					panelClass = "";
					break;

				default:
					break;
			}

			const topModal = await this.modalCtrl.getTop();
			if (topModal) {
				await this.modalCtrl.dismiss(dismissData);
			} else {
				console.warn("Modal already dismissed or not present.");
			}

			if (messageKey) {
				this.snackbar.open(this.STranslate.instant(messageKey), "", {
					duration: 3000,
					panelClass: panelClass
				});
			}
		} catch (error) {
			console.error("Error dismissing modal:", error);
		}
	}

	/**
	 * downloadDocument
	 * @param fieldId
	 */
	async downloadDocument(url: string, docName: string) {
		if (!url || typeof url !== "string" || url.startsWith("pending://")) {
			this.snackbar.open(this.STranslate.instant("snackbar.no_document_to_download"), "", {
				duration: 3000,
				panelClass: "warning-snackbar"
			});
			return;
		}

		try {
			await firstValueFrom(this.FileService.downloadFileFromUrl(url, docName ?? "document"));
		} catch (error) {
			console.error("Error downloading document:", error);
			this.snackbar.open(this.STranslate.instant("snackbar.error-download"), "", {
				duration: 3000,
				panelClass: "error-snackbar"
			});
		}
	}

	// --- UI Helper Methods for Documents ---

	/**
	 * Get the display name for a document field.
	 */
	getDocumentName(fieldId: string): string {
		return this.documentUIState[fieldId]?.name || "";
	}

	/**
	 * Get the asset file name (icon) for a document field.
	 */
	getAssetFileName(fieldId: string): string {
		// Return specific icon if available, otherwise generic
		return this.documentUIState[fieldId]?.assetFileName || "generic-file_ico";
	}

	/**
	 * Check if the document (existing or pending) for a field is an image.
	 * This might not be strictly necessary if getDocumentPreviewUrl handles it.
	 */
	isDocumentImage(fieldId: string): boolean {
		const state = this.documentUIState[fieldId];
		return state?.type?.includes("image") ?? false;
	}

	/**
	 * Check if this field has a pending file upload
	 */
	hasPendingFile(fieldId: string): boolean {
		return !!this.filesToUpload[fieldId];
	}

	/**
	 * Get the preview URL for a document field (either existing image URL or generated base64 preview).
	 */
	getDocumentPreviewUrl(fieldId: string): string | undefined | null {
		// Return the stored previewUrl from the UI state
		return this.documentUIState[fieldId]?.previewUrl;
	}

	// --- End UI Helper Methods ---
}
